import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, ArrowRight } from 'lucide-react';
import ListingCard from '../components/ListingCard';
import CornerOrnament from '../components/CornerOrnament';
import { getImageUrl } from '../lib/utils-img';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import API from '../lib/api';

export default function LocationDetailPage() {
  const { slug } = useParams();
  const [location, setLocation] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageContent, setPageContent] = useState({});

  useEffect(() => {
    setLoading(true);
    Promise.all([
      API.get(`/locations/${slug}`),
      API.get(`/pages/location_${slug}`).catch(() => ({ data: {} })),
    ]).then(([locRes, pageRes]) => {
      setLocation(locRes.data.location);
      setListings(locRes.data.listings);
      setPageContent(pageRes.data || {});
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-[#101010] flex items-center justify-center"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>;
  if (!location) return <div className="min-h-screen bg-[#101010] flex flex-col items-center justify-center gap-4"><p className="text-white/55 text-lg">Location not found.</p><Link to="/locations" className="text-gold text-sm">View all locations</Link></div>;

  const pc = (key, fallback) => pageContent[key] || fallback;

  return (
    <div data-testid="location-detail-page" className="bg-[#101010]">
      <SEO
        title={`Luxury Rentals in ${location.name}`}
        path={`/locations/${slug}`}
        description={location.full_description || location.short_description || `Discover premium luxury short-term rentals in ${location.name}, Ontario.`}
        keywords={`luxury rentals ${location.name}, short-term rental ${location.name}, vacation rental ${location.name}`}
        image={location.hero_image}
      />
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[450px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={getImageUrl(location.hero_image)} alt={location.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#101010] via-black/40 to-black/20" />
        </div>
        <div className="relative z-10 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <Link to="/locations" className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-xs uppercase tracking-[0.2em] mb-5 transition-colors font-medium" data-testid="back-to-locations">
            <ArrowLeft className="w-3.5 h-3.5" /> All Locations
          </Link>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-3 mb-3">
              <MapPin className="w-4 h-4 text-gold" />
              <span className="text-gold text-[11px] uppercase tracking-[0.3em] font-bold">Ontario, Canada</span>
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl text-white font-bold leading-[1.05]">{location.name}</h1>
          </motion.div>
        </div>
      </section>

      {/* Description */}
      {location.full_description && (
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              <div>
                <p className="text-gold text-[11px] uppercase tracking-[0.3em] font-bold mb-3">{pc('__about_label', 'About')}</p>
                <h2 className="font-heading text-3xl text-white font-bold">{location.name}</h2>
              </div>
              <div className="lg:col-span-2">
                <p className="text-white/50 text-base sm:text-lg leading-relaxed whitespace-pre-line">{location.full_description}</p>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Listings Grid */}
      <section className="py-20 lg:py-28 bg-[#161616]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
            <div>
              <p className="text-gold text-[11px] uppercase tracking-[0.3em] font-bold mb-3">{pc('__properties_label', 'Available Properties')}</p>
              <h2 className="font-heading text-4xl sm:text-5xl text-white font-bold">Stay in <span className="italic text-gold">{location.name}</span></h2>
            </div>
            <p className="text-white/35 text-sm">{listings.length} {listings.length === 1 ? 'property' : 'properties'} available</p>
          </motion.div>
          {listings.length === 0 ? (
            <div className="text-center py-20 bg-[#1c1c1c] rounded-xl border border-white/[0.08]">
              <p className="text-white/55 text-lg mb-2">No properties available yet.</p>
              <p className="text-white/30 text-sm">Check back soon for new listings in {location.name}.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-4 text-center relative">
          <CornerOrnament size={28} offset="0px" />
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="py-8">
            <h3 className="font-heading text-3xl sm:text-4xl text-white font-bold mb-4">
              {pc('__cta_title', 'Need Help')} <span className="italic text-gold">{pc('__cta_title', '').includes(location.name) ? '' : 'Choosing?'}</span>
            </h3>
            <p className="text-white/45 mb-8 max-w-md mx-auto">
              {pc('__cta_description', `Our team is happy to help you find the perfect property for your stay in ${location.name}.`)}
            </p>
            <Link to="/contact">
              <Button className="bg-gold hover:bg-gold-dark text-white h-13 px-8 text-xs uppercase tracking-[0.15em] font-semibold rounded-full transition-all duration-300 shadow-lg shadow-gold/20" data-testid="location-contact-cta">
                {pc('__cta_btn', 'Contact Us')} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
